import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { profileService, Profile } from '../../../services/profileService';
import { useRights } from '../../../hooks/useRights';
import { useHasAnyActionRight, profileActions } from '../../../utils/actionRightsUtils';
import { Pencil } from 'lucide-react';
import TableActions from '../../../components/TableActions';
import '../../../styles/index.css';
import './Profiles.css';

interface Right {
  value: string;
  label: string;
}

interface RightsGroup {
  category: string;
  prefix: string;
  rights: Right[];
}

const Profiles: React.FC = () => {
  const { t } = useTranslation();
  const { hasRight } = useRights();
  const hasAnyActionRight = useHasAnyActionRight(profileActions());

  const [profiles, setProfiles] = useState<Profile[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editingProfile, setEditingProfile] = useState<Profile | null>(null);
  const [editForm, setEditForm] = useState({ name: '', rights: [] as string[], type: '' as 'TEACHER' | 'ADMIN' | 'STUDENT' | '' });
  const [isCreating, setIsCreating] = useState(false);
  const [createForm, setCreateForm] = useState({ name: '', rights: [] as string[], type: '' as 'TEACHER' | 'ADMIN' | 'STUDENT' | '' });
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());
  const [editCollapsedGroups, setEditCollapsedGroups] = useState<Set<string>>(new Set());

  useEffect(() => {
    loadProfiles();
  }, []);

  const loadProfiles = async () => {
    try {
      setLoading(true);
      const data = await profileService.getAllProfiles();
      setProfiles(data);
      setError('');
    } catch (e: any) {
      setError(t('profiles.failedToLoad') + ': ' + (e.response?.data?.message || e.message));
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (profile: Profile) => {
    setEditingProfile(profile);
    setEditForm({
      name: profile.name,
      rights: [...profile.rights],
      type: profile.type || '',
    });
    setEditCollapsedGroups(new Set());
  };

  const handleCancelEdit = () => {
    setEditingProfile(null);
    setEditForm({ name: '', rights: [], type: '' });
  };

  const handleCreate = () => {
    setIsCreating(true);
    setCreateForm({ name: '', rights: [], type: '' });
    setCollapsedGroups(new Set());
  };

  const handleCancelCreate = () => {
    setIsCreating(false);
    setCreateForm({ name: '', rights: [], type: '' });
  };

  const handleSaveCreate = async () => {
    if (!createForm.name.trim()) {
      setError(t('profiles.profileNameRequired'));
      return;
    }
    if (!createForm.type) {
      setError(t('profiles.profileTypeRequired'));
      return;
    }

    try {
      await profileService.createProfile({
        name: createForm.name,
        rights: createForm.rights,
        type: createForm.type,
      });
      await loadProfiles();
      handleCancelCreate();
    } catch (e: any) {
      setError(t('profiles.failedToCreate') + ': ' + (e.response?.data?.message || e.message));
    }
  };

  const toggleCreateRight = (right: string) => {
    setCreateForm((prev) => ({
      ...prev,
      rights: prev.rights.includes(right)
        ? prev.rights.filter((r) => r !== right)
        : [...prev.rights, right],
    }));
  };

  const handleSave = async () => {
    if (!editingProfile) return;
    if (!editForm.type) {
      setError(t('profiles.profileTypeRequired'));
      return;
    }

    try {
      await profileService.updateProfile(editingProfile.id, {
        name: editForm.name,
        rights: editForm.rights,
        type: editForm.type,
      });
      await loadProfiles();
      handleCancelEdit();
    } catch (e: any) {
      setError(t('profiles.failedToUpdate') + ': ' + (e.response?.data?.message || e.message));
    }
  };

  const toggleRight = (right: string) => {
    setEditForm((prev) => ({
      ...prev,
      rights: prev.rights.includes(right)
        ? prev.rights.filter((r) => r !== right)
        : [...prev.rights, right],
    }));
  };

  const toggleCollapseGroup = (category: string, isEdit: boolean = false) => {
    if (isEdit) {
      setEditCollapsedGroups((prev) => {
        const newSet = new Set(prev);
        if (newSet.has(category)) {
          newSet.delete(category);
        } else {
          newSet.add(category);
        }

        return newSet;
      });
    } else {
      setCollapsedGroups((prev) => {
        const newSet = new Set(prev);
        if (newSet.has(category)) {
          newSet.delete(category);
        } else {
          newSet.add(category);
        }
        return newSet;
      });
    }
  };

  // Rights grouped by category - memoized to avoid recreation on every render
  const rightsGroups: RightsGroup[] = useMemo(() => [
    {
      category: t('profiles.rightsGroups.admins'),
      prefix: 'admins',
      rights: [
        { value: 'admins:r', label: t('profiles.rights.read') },
        { value: 'admins:u', label: t('profiles.rights.update') },
        { value: 'admins:c', label: t('profiles.rights.create') },
        { value: 'admins:d', label: t('profiles.rights.delete') },
      ],
    },
    {
      category: t('profiles.rightsGroups.courses'),
      prefix: 'courses',
      rights: [
        { value: 'courses:r', label: t('profiles.rights.read') },
        { value: 'courses:u', label: t('profiles.rights.update') },
        { value: 'courses:c', label: t('profiles.rights.create') },
        { value: 'courses:d', label: t('profiles.rights.delete') },
      ],
    },
    {
      category: t('profiles.rightsGroups.lessons'),
      prefix: 'lessons',
      rights: [
        { value: 'lessons:r', label: t('profiles.rights.read') },
        { value: 'lessons:u', label: t('profiles.rights.update') },
        { value: 'lessons:c', label: t('profiles.rights.create') },
        { value: 'lessons:d', label: t('profiles.rights.delete') },
      ],
    },
    {
      category: t('profiles.rightsGroups.scores'),
      prefix: 'scores',
      rights: [
        { value: 'scores:r', label: t('profiles.rights.read') },
        { value: 'scores:u', label: t('profiles.rights.update') },
        { value: 'scores:c', label: t('profiles.rights.create') },
        { value: 'scores:d', label: t('profiles.rights.delete') },
      ],
    },
    {
      category: t('profiles.rightsGroups.classes'),
      prefix: 'classes',
      rights: [
        { value: 'classes:r', label: t('profiles.rights.read') },
        { value: 'classes:u', label: t('profiles.rights.update') },
        { value: 'classes:c', label: t('profiles.rights.create') },
        { value: 'classes:d', label: t('profiles.rights.delete') },
      ],
    },
    {
      category: t('profiles.rightsGroups.levels'),
      prefix: 'levels',
      rights: [
        { value: 'levels:r', label: t('profiles.rights.read') },
        { value: 'levels:u', label: t('profiles.rights.update') },
        { value: 'levels:c', label: t('profiles.rights.create') },
        { value: 'levels:d', label: t('profiles.rights.delete') },
      ],
    },
    {
      category: t('profiles.rightsGroups.students'),
      prefix: 'students',
      rights: [
        { value: 'students:r', label: t('profiles.rights.read') },
        { value: 'students:u', label: t('profiles.rights.update') },
        { value: 'students:c', label: t('profiles.rights.create') },
        { value: 'students:d', label: t('profiles.rights.delete') },
      ],
    },
    {
      category: t('profiles.rightsGroups.teachers'),
      prefix: 'teachers',
      rights: [
        { value: 'teachers:r', label: t('profiles.rights.read') },
        { value: 'teachers:u', label: t('profiles.rights.update') },
        { value: 'teachers:c', label: t('profiles.rights.create') },
        { value: 'teachers:d', label: t('profiles.rights.delete') },
      ],
    },
    {
      category: t('profiles.rightsGroups.bulletins'),
      prefix: 'bulletins',
      rights: [
        { value: 'bulletins:r', label: t('profiles.rights.read') },
        { value: 'bulletins:c', label: t('profiles.rights.create') },
      ],
    },
    {
      category: t('profiles.rightsGroups.bills'),
      prefix: 'bills',
      rights: [
        { value: 'bills:r', label: t('profiles.rights.read') },
        { value: 'bills:u', label: t('profiles.rights.update') },
        { value: 'bills:c', label: t('profiles.rights.create') },
        { value: 'bills:d', label: t('profiles.rights.delete') },
      ],
    },
    {
      category: t('profiles.rightsGroups.absences'),
      prefix: 'absences',
      rights: [
        { value: 'absences:r', label: t('profiles.rights.read') },
        { value: 'absences:u', label: t('profiles.rights.update') },
        { value: 'absences:c', label: t('profiles.rights.create') },
        { value: 'absences:d', label: t('profiles.rights.delete') },
      ],
    },
    {
      category: t('profiles.rightsGroups.profiles'),
      prefix: 'profiles',
      rights: [
        { value: 'profiles:r', label: t('profiles.rights.read') },
        { value: 'profiles:u', label: t('profiles.rights.update') },
        { value: 'profiles:c', label: t('profiles.rights.create') },
        { value: 'profiles:d', label: t('profiles.rights.delete') },
      ],
    },
    {
      category: t('profiles.rightsGroups.administration'),
      prefix: 'administration',
      rights: [
        { value: 'administration:r', label: t('profiles.rights.read') },
        { value: 'administration:u', label: t('profiles.rights.update') },
        { value: 'administration:c', label: t('profiles.rights.create') },
        { value: 'administration:d', label: t('profiles.rights.delete') },
      ],
    },
  ], [t]);

  const RightsGroup: React.FC<{
    group: RightsGroup;
    selectedRights: string[];
    onToggle: (right: string) => void;
    collapsedGroups: Set<string>;
    onToggleCollapse: (category: string) => void;
  }> = ({ group, selectedRights, onToggle, collapsedGroups, onToggleCollapse }) => {
    const isCollapsed = collapsedGroups.has(group.category);

    return (
      <div className="rights-group">
        <div
          className="rights-group-header"
          onClick={() => onToggleCollapse(group.category)}
        >
          <span className="collapse-icon">{isCollapsed ? '▶' : '▼'}</span>
          <span className="rights-group-title">{group.category}</span>
        </div>
        {!isCollapsed && (
          <div className="rights-group-content">
            {group.rights.map((right) => (
              <label key={right.value} className="right-checkbox">
                <input
                  type="checkbox"
                  checked={selectedRights.includes(right.value)}
                  onChange={() => onToggle(right.value)}
                />
                {right.label}
              </label>
            ))}
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return <div className="profiles-container">{t('common.loading')}</div>;
  }

  return (
    <div className="profiles-container">
      <div className="bo-header">
        <h2>{t('profiles.title')}</h2>
        <button onClick={handleCreate} className="btn-create" disabled={isCreating || editingProfile !== null}>
          + {t('profiles.createNew')}
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}

      {isCreating && (
        <div className="modal-overlay" onClick={handleCancelCreate}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{t('profiles.createProfile')}</h3>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>{t('common.name')}:</label>
                <input
                  type="text"
                  value={createForm.name}
                  onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
                  placeholder={t('profiles.profileName')}
                  autoFocus
                />
              </div>
              <div className="form-group">
                <label>{t('profiles.type')} *</label>
                <select
                  value={createForm.type || ''}
                  onChange={(e) => setCreateForm({ ...createForm, type: e.target.value as any })}
                  required
                >
                  <option value="">{t('common.select')}</option>
                  <option value="TEACHER">{t('common.teacher')}</option>
                  <option value="ADMIN">{t('common.admin')}</option>
                  <option value="STUDENT">{t('common.student')}</option>
                </select>
              </div>
              <div className="form-group">
                <label>{t('profiles.rightsLabel')}:</label>
                <div className="rights-groups-container">
                  {rightsGroups.map((group) => (
                    <RightsGroup
                      key={group.category}
                      group={group}
                      selectedRights={createForm.rights}
                      onToggle={toggleCreateRight}
                      collapsedGroups={collapsedGroups}
                      onToggleCollapse={(cat) => toggleCollapseGroup(cat, false)}
                    />
                  ))}
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button onClick={handleSaveCreate} className="btn-primary">{t('common.create')}</button>
              <button onClick={handleCancelCreate} className="btn-secondary">{t('common.cancel')}</button>
            </div>
          </div>
        </div>
      )}

      {editingProfile && (
        <div className="modal-overlay" onClick={handleCancelEdit}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{t('profiles.editProfile')}</h3>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>{t('common.name')}:</label>
                <input
                  type="text"
                  value={editForm.name}
                  onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>{t('profiles.type')} *</label>
                <select
                  value={editForm.type || ''}
                  onChange={(e) => setEditForm({ ...editForm, type: e.target.value as any })}
                  required
                >
                  <option value="">{t('common.select')}</option>
                  <option value="TEACHER">{t('common.teacher')}</option>
                  <option value="ADMIN">{t('common.admin')}</option>
                  <option value="STUDENT">{t('common.student')}</option>
                </select>
              </div>
              <div className="form-group">
                <label>{t('profiles.rightsLabel')}:</label>
                <div className="rights-groups-container">
                  {rightsGroups.map((group) => (
                    <RightsGroup
                      key={group.category}
                      group={group}
                      selectedRights={editForm.rights}
                      onToggle={toggleRight}
                      collapsedGroups={editCollapsedGroups}
                      onToggleCollapse={(cat) => toggleCollapseGroup(cat, true)}
                    />
                  ))}
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button onClick={handleSave} className="btn-primary">{t('common.save')}</button>
              <button onClick={handleCancelEdit} className="btn-secondary">{t('common.cancel')}</button>
            </div>
          </div>
        </div>
      )}

      <div className="profiles-table-container">
        {profiles.length === 0 ? (
          <p>{t('profiles.noProfilesFound')}</p>
        ) : (
          <table className="profiles-table">
            <thead>
              <tr>
                <th>{t('common.name')}</th>
                <th>{t('profiles.type')}</th>
                {hasAnyActionRight && <th>{t('common.actions')}</th>}
              </tr>
            </thead>
            <tbody>
              {profiles.map((profile) => (
                <tr key={profile.id}>
                  <td>{profile.name}</td>
                  <td>{t(`common.${profile?.type?.toLowerCase()}`)}</td>
                  {hasAnyActionRight && (
                    <td>
                      <TableActions actions={[
                        {
                          label: t('common.edit'),
                          onClick: () => handleEdit(profile),
                          className: 'btn-edit',
                          icon: <Pencil size={16} />,
                          hideLabelDesktop: true
                        }
                      ]} />
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default Profiles;

